import Preloader from 'components/Dashboard/Analytics/ui/AnalyticsDashboard/Preloader';
import CreditsButton from 'components/Dashboard/components/CreditsButton';
import BriefingItem from 'components/Dashboard/DashboardComponent/BriefingItem';
import { searchIncludesTitleConditional } from 'components/Dashboard/StakeHolders/searchIncludesTitle';
import getUrlParam, { decodeUrlQueryValue, getUrlParamRaw } from 'components/Dashboard/utilities/getUrlParam';
import time from 'utils/time';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { store } from 'components/Store';
import { useContext, useRef, useState } from 'react';
import { useSiteSectionsNames } from 'utils/getAppSite';
import { removeWrappingQuotes } from 'utils/stringFunctions';
import { useNavigate } from 'react-router';

const StakeholderAISummary = ({
  StakeholderInformation,
  generateSummary,
  isLoadingStakeholderAISummary: isLoading,
  currentlyProcessing,
  generatingSummary,
  content,
}) => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { keywordsLists, remainingCredits, team } = state;

  const { name, officalName, categories } = StakeholderInformation;

  const topicParameter = getUrlParam('topic-id');
  const searchParameter = getUrlParamRaw('search') ? decodeUrlQueryValue(getUrlParamRaw('search')) : '';
  const hasSearchQuery = !!searchParameter?.trim();
  const topic = keywordsLists?.find((item) =>
    topicParameter === 'all' ? item?.id === null : item?.id === parseInt(topicParameter)
  );

  const navigate = useNavigate();
  const { getTopicTitle } = useSiteSectionsNames();

  const specialAdviser = categories?.includes('Special Adviser');
  const searchCondition =
    (!!topicParameter || hasSearchQuery) && !searchIncludesTitleConditional(officalName) && !specialAdviser;

  const relationText = () => {
    const safeSearchQuery = hasSearchQuery ? searchParameter.trim() : '';
    if (topic?.name && hasSearchQuery) {
      return `${getTopicTitle(topic?.name)} and "${removeWrappingQuotes(safeSearchQuery)}"`;
    }
    if (topic?.name) {
      return getTopicTitle(topic?.name);
    }
    if (hasSearchQuery) {
      return `"${removeWrappingQuotes(safeSearchQuery)}"`;
    }
    return 'all topics';
  };

  const usernameAndTopic = (props) => {
    const { lineBreak } = props ?? {};
    return (
      <>
        {name}
        {name?.endsWith('s') ? "'" : "'s"} {lineBreak ? <br /> : ''} activity in relation to: {relationText()}
      </>
    );
  };

  return (
    <div className={`px-4 px-lg-5 flex-grow-1 d-flex flex-column ${searchCondition ? 'pt-2' : ''}`}>
      {searchCondition ? (
        <div className='pb-4 flex-grow-1 d-flex flex-column'>
          {isLoading ? (
            <Preloader />
          ) : (
            <>
              {generatingSummary || currentlyProcessing?.state ? (
                <>
                  <div className='flex-grow-1 flex-centered flex-column justify-content-center'>
                    <div>
                      <div className='text-center pb-5 mx-auto title-h5-m' style={{ marginTop: '-70px' }}>
                        <p className='mb-2 mx-auto' style={{ maxWidth: '450px' }}>
                          <strong>We’re generating a summary of {usernameAndTopic({ lineBreak: true })}</strong>
                        </p>
                        <p className='mx-auto pt-2' style={{ maxWidth: '350px', lineHeight: '1.2' }}>
                          <span className='light-50-text'>
                            This could take a minute or two. Feel free to navigate away and come back
                          </span>
                        </p>
                      </div>
                      <div className='waiting-bar mx-auto'>
                        <div className='waiting-bar-indicator' />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {!!content ? (
                    <>
                      <p className='mb-4 rounded  py-2 px-4 paragraph-p2 rounded-pill flex-centered justify-content-between ai-summary-note'>
                        AI summaries draw on PolicyMogul data but should still be reviewed for accuracy
                        <span
                          data-tooltip-class-name='pricing-feature-tooltip feed-tooltip billing-tooltip'
                          data-tooltip-content={`This summary draws on up-to-date information from PolicyMogul, your organisation description, and biographical details about the stakeholder. As with all AI output, it should be reviewed carefully to ensure accuracy.`}
                          data-tooltip-id={'general-tooltip'}
                          className='icon-info ml-2'
                        />
                      </p>
                      <div className='flex-centered'>
                        <h3 className='title-h5-bold mb-0 mr-1'>Summary of {usernameAndTopic()}</h3>
                        <button
                          className='general-button circle-button hovered-button d-inline-block-centered specific-top-margin-2'
                          data-tooltip-id='general-tooltip'
                          data-tooltip-content={'Add stakeholder summary to a report'}
                          onClick={(e) => {
                            dispatch({
                              type: 'MODIFY_SECTION',
                              parameter: 'stakeholderToCreateReport',
                              value: {
                                type: 'StakeholderAISummariesComponent',
                                stakeholders: [StakeholderInformation],
                                topic,
                              },
                            });
                            navigate({
                              pathname: '/reports/create',
                            });
                          }}
                        >
                          <span className='icon-document' />
                        </button>
                      </div>

                      <RegenerateDropdown content={content} generateSummary={generateSummary} />
                      <p className='bg-main-blue rounded px-3 text-white specific-y-padding-7'>Overview</p>
                      <BriefingItem
                        notShowThumbsDown={true}
                        openLinksInNewTab={true}
                        item={{ body: content?.summary }}
                        noSpacing={true}
                      />
                      {content?.teamSection && (
                        <>
                          <p className='bg-main-blue rounded px-3 text-white specific-y-padding-7'>
                            Focus on {team?.teamName}
                          </p>{' '}
                          <BriefingItem
                            notShowThumbsDown={true}
                            item={{ body: content?.teamSection }}
                            noSpacing={true}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <h3 className='category-subtitle mb-0 line-height-1-5 '>
                        Generate a tailored summary of {usernameAndTopic()}
                      </h3>

                      <div
                        className='border rounded flex-centered hovered-button pl-4 pr-3 py-3'
                        onClick={() => {
                          generateSummary();
                        }}
                      >
                        <img
                          style={{ width: '16px' }}
                          className='specific-right-margin-9 '
                          src={`${import.meta.env.VITE_APP_CDNURL}/images/20231216-prospecting-icon.png`}
                          alt='Prospecting icon'
                        />
                        <button className='general-button text-left flex-grow-1 d-block text-light-blue'>
                          {topicParameter === 'all'
                            ? 'Generate a tailored summary based on all topics'
                            : topic?.name
                              ? 'Generate tailored summary for this topic'
                              : 'Generate tailored summary for this search'}
                        </button>
                        {remainingCredits >= 0 && <CreditsButton type={'AI'} className={'credits-button-summary'} />}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      ) : (
        <p className='text-center title-h5 light-70-text mx-auto no-activity-message'>
          {specialAdviser ? (
            'AI summaries are not available for Special Advisers'
          ) : (
            <>
              Choose a <span className='font-weight-bold'>topic</span> or{' '}
              <span className='font-weight-bold'>keyword</span> above to generate an AI
              <br /> summary of this stakeholder's activity in relation to it
            </>
          )}
        </p>
      )}
    </div>
  );
};

const RegenerateDropdown = ({ generateSummary, content }) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { remainingCredits } = state;

  const { updatedAtUtc } = content ?? {};

  const [isOpen, setIsOpen] = useState(false);
  const hideOptions = () => {
    setIsOpen(false);
  };

  const actionsElement = useRef(null);
  useOutsideElement(actionsElement, hideOptions);

  return (
    <div className='mb-2 flex-centered light-50-text'>
      <p className='mb-0 paragraph-p2'>Last updated: {time(updatedAtUtc, { utc: true }).formatLongDateWithTime()} </p>
      <div
        className='position-relative'
        style={{
          visibility: time(content?.updatedAtUtc, { utc: true }).isSame(time(undefined, { utc: true }), 'day')
            ? 'hidden'
            : 'visible',
        }}
      >
        <button
          className='general-button cirlce-button circle-button-36 hovered-button specific-top-margin-3'
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <span className='icon-three-dots' />
        </button>
        {isOpen && (
          <>
            <div
              className='bg-main-white border rounded flex-centered hovered-button pl-3 pr-2 specific-y-padding-12 position-absolute regenerate-options-dropdown width-fit-content'
              ref={actionsElement}
              onClick={() => {
                generateSummary();
              }}
            >
              <img
                style={{ width: '16px' }}
                className='specific-right-margin-9 '
                src={`${import.meta.env.VITE_APP_CDNURL}/images/20231216-prospecting-icon.png`}
                alt='Prospecting icon'
              />
              <button className='general-button text-left flex-grow-1 d-block text-light-blue mr-4'>Regenerate</button>
              {remainingCredits >= 0 && <CreditsButton type={'AI'} className={'credits-button-summary'} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StakeholderAISummary;
